import { timeFormat, timeParse } from 'd3-time-format';
import { format } from 'd3-format';

// -- datetime --------------------
let systemDateFormat = '%m/%d/%Y';
let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
let serverTimeZone = 'America/Los_Angeles';

export function setTimeZone(v) {
  timeZone = v;
}

export function setDateFormat(f) {
  const options = { 'DD/MM/YYYY': '%d/%m/%Y', 'MM/DD/YYYY': '%m/%d/%Y' };
  systemDateFormat = options[f] || '%m/%d/%Y';
}
export function getDateFormat(f) {
  const options = { 'DD/MM/YYYY': '%d/%m/%Y', 'MM/DD/YYYY': '%m/%d/%Y' };
  return options[f] || '%m/%d/%Y';
}

const timeFormatCache = {};

export function setServerTimeZone(v) {
  serverTimeZone = v;
}

function getServerTimezoneOffsetMinutes(timeZoneName) {
  const d = new Date();
  const utcDate = new Date(d.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(d.toLocaleString('en-US', { timeZone: timeZoneName }));
  const offset = (tzDate.getTime() - utcDate.getTime()) / 6e4;
  return -1 * offset;
}

export function getDate(dateYMD) {
  // special logic for Safari
  if (!dateYMD || dateYMD?.length < 10) return ''; // 10 for short date 2000-01-01
  if (dateYMD?.length === 10) dateYMD += ' 00:00:00';
  if (dateYMD[4] !== '-') return new Date(dateYMD);

  let dateSafari = dateYMD.replace(' ', 'T');
  const date = new Date(dateSafari);
  return date;
}
export function formatDateDef(value, formatStr = systemDateFormat) {
  if (!value) return '';

  if (formatStr) {
    let format = timeFormatCache[formatStr];
    if (format == null) {
      format = timeFormat(formatStr);
      timeFormatCache[formatStr] = format;
    }
    if (typeof (value) === 'string') {
      value = getDate(value);
    }
    return format(new Date(value));
  }
  return value;
}

export function parseDateValue(value, formatStr = '%m/%d/%Y') {
  if (value === '') return null;

  if ((value || '').length == 10) {
    let v = timeParse(formatStr);
    return v(value);
  }
  return null;
}

export function formatDatetimeDef(datetimeYMD) {
  if (!datetimeYMD) return '';

  if (datetimeYMD === 'now') datetimeYMD = getDateCreate('now');

  const f = getDateFormat() + ' %I:%M %p';
  const dateFormatted = formatDateDef(datetimeYMD, f);
  return dateFormatted;
}

export function getDateCreate(dateISO) {
  if (!dateISO) return '';
  const d = dateISO === 'now' ? new Date() : new Date(dateISO);
  const serverTimeZoneOffset = getServerTimezoneOffsetMinutes(serverTimeZone);
  const clientTimeZoneOffset = d.getTimezoneOffset(); // for debugging
  // console.log('d1:', serverTimeZoneOffset / 60, clientTimeZoneOffset / 60, serverTimeZone, d.toISOString());
  d.setMinutes(d.getMinutes() - serverTimeZoneOffset);
  let newValue = d.toISOString().replace('T', ' ').replace('Z', '');
  return newValue;
}

export function formatDatetimeRel(datetime, addZero) {
  if (!datetime) return '';
  let value = datetime;
  // if (value.length === 23) value = value.replace(' ', 'T') + 'Z';

  const d = getDate(value);
  const now = new Date();
  if (!d || !d.getDate()) return value;
  let day = d.getDate();
  day = day < 10 && addZero ? '0' + day : day;

  const diffMS = now - d;
  const diffS = Math.round((now - d) / 1000, 0);
  const diffMins = Math.round(diffMS / 60000, 0);
  let dateFormatted = '';

  // future, same year
  if (diffMS < 0 && d.getFullYear() == now.getFullYear()) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    dateFormatted = months[d.getMonth()] + ' ' + day;
  }
  // future, another year
  else if (diffMS < 0) {
    dateFormatted = formatDateDef(value);
  }

  // -- past --
  // less than a minute ago
  else if (diffMS < 60000) {
    dateFormatted = (diffS) + ' sec. ago';
  }
  // less than an hour ago
  else if (diffMins < 60) {
    dateFormatted = diffMins + ' min. ago';
  }
  // same day, but more than an hour ago
  else if (d.getDate() == now.getDate() && d.getMonth() == now.getMonth() && d.getFullYear() == now.getFullYear()) {
    dateFormatted = formatDateDef(value, '%I:%M %p');

    if (dateFormatted?.includes('NaN') && root.app.statsAddErrorLimited) {
      root.app.statsAddErrorLimited({
        errorName: 'formatDatetimeRel_hour+',
        errorLimit: 2,
        errorInfo: { message: 'format relative: hour+', dateFinalFormatted: dateFormatted, dateOriginal: datetime, getDate: d, now, day, diffMS, diffS, diffMins },
      });
    }
  }
  // same year
  else if (d.getFullYear() == now.getFullYear()) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    dateFormatted = months[d.getMonth()] + ' ' + day;
  }

  else {
    const f = getDateFormat();
    dateFormatted = formatDateDef(value, f);
  }

  // console.log('date:', dateFormatted);
  if (dateFormatted?.includes('NaN') && root.app.statsAddErrorLimited) {
    root.app.statsAddErrorLimited({
      errorName: 'formatDatetimeRel_',
      errorLimit: 2,
      errorInfo: { message: 'format relative', dateFinalFormatted: dateFormatted, dateOriginal: datetime, getDate: d, now, day, diffMS, diffS, diffMins },
    });
  }
  return dateFormatted;
}


export function getTimeDiff(t1, t2, units = 'ms') {
  if (!t1 || !t2 || !(getDate(t1)) || !(getDate(t2))) return null;
  const timeDiffMS = (getDate(t1)).getTime() - (getDate(t2)).getTime();
  if (units === 'ms') return timeDiffMS;
  if (units === 'seconds') return timeDiffMS / 1000;
  if (units === 'minutes') return timeDiffMS / 1000 / 60;
  if (units === 'hours') return timeDiffMS / 1000 / 3600;
  if (units === 'days') return timeDiffMS / 1000 / 3600 / 24;
  return null;
}

export function getDateAdd(dateUTC, n, units) {
  if (!dateUTC) dateUTC = (new Date()).toISOString();
  n = Number(n) || 0;

  let d1 = getDate(dateUTC);
  d1.setMinutes(d1.getMinutes() - d1.getTimezoneOffset());
  // console.log('add:', dateUTC, d1.toISOString());
  if (units === 'hours') d1.setHours(d1.getHours() + n);
  if (units === 'days') d1.setDate(d1.getDate() + n);
  if (units === 'months') d1.setMonth(d1.getMonth() + n);
  if (units === 'years') d1.setFullYear(d1.getFullYear() + n);
  if (units === 'minutes') d1.setMinutes(d1.getMinutes() + n);
  // console.log('add2:', d1.toISOString());
  let newValue = d1.toISOString().replace('T', ' ').replace('Z', '');
  return newValue;
}


export function getTime(isoDate) {
  if (!isoDate) return '';
  return isoDate.substring(11, 19);
}

export function getAgeYrs(date) {
  if (!date || !(getDate(date))) return date;
  return (((new Date()).getTime() - (getDate(date)).getTime()) / 365 / 24 / 3600000).toFixed(0);
}

export function esriDate(d) {
  if (d == null || d == '') return null;
  return formatValue(d, 'date');
}


// -- address ---------------------------
export function getWOAddressLine(row) {
  return [
    (row.site_street_number || row.site_street_name) ? (row.site_street_number || '') + ' ' + (row.site_street_name || '') : null,
    row.site_cross_street,
    row.site_city,
    // row.site_state,
    row.site_zip_code,
    row.site_address_2,
  ].filter(Boolean).join(', ');
}
export function getRequestorAddressLine(row) {
  return [
    (row.req_street_number || row.req_street_name) ? (row.req_street_number || '') + ' ' + (row.req_street_name || '') : null,
    row.req_cross_street,
    row.req_city,
    // row.site_state,
    row.req_zip,
    row.req_address_2,
  ].filter(Boolean).join(', ');
}


// -- format value ------------------------
const numberFormatCache = {};
function formatNumberValue(value, formatStr) {
  if (!value && value != 0) return '';
  if (formatStr) {
    let f = numberFormatCache[formatStr];
    if (f == null) {
      f = format(formatStr);
      numberFormatCache[formatStr] = f;
    }
    return f(value);
  }
  return value;
}

function formatCheckValue(value, formatStr) {
  let v = '';
  if (formatStr === 'Y/N') {
    v = [true, 'true', 1, '1', 'Y'].indexOf(value) !== -1 ? 'Y' : 'N';
  } else if (formatStr === '1/0') {
    v = [true, 'true', 1, '1', 'Y'].indexOf(value) !== -1 ? 1 : 0;
  }
  return v || value;
}

export function formatValue(value, type, formatStr) {
  // console.log('format:', value, type);
  if (value === '') return '';
  if (value || value == 0) {
    if (type === 'date') { return formatDateDef(value, formatStr); }
    if (type === 'datetime') { return formatDatetimeRel(value); }
    if (type === 'number') { return formatNumberValue(value, formatStr); }
    if (type === 'boolean') { return formatCheckValue(value, formatStr); }
    if (formatStr) {
      const f = format(formatStr);
      return f(value);
    }
  }
  return value;
}
